import type { PropertyApiResponse } from "@/features/properties/model/propertiesTypes";
import { apiClient } from "@/services/api/axiosClient";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import type { NewsServiceResponse, NewsArticle, SerpApiResponse } from "../modal/news.types";

const SERP_SEARCH_PATH = API_ENDPOINTS.PROPERTY.SERP_SEARCH;

export const fetchNews = async (
  property?: PropertyApiResponse | null
): Promise<NewsServiceResponse> => {
  try {
    const propertyToUse = property;
    const defaultLocation = "Austin, TX";

    const city = propertyToUse?.address?.city;
    const zip = propertyToUse?.address?.zip;
    const userLocation =
      zip || (typeof city === "string" ? city.trim() || defaultLocation : defaultLocation);

    const topics =
      "property taxes real estate neighborhood local crime sex offenders registered offenders public safety";
    let q = `latest local news about ${topics}`;
    if (propertyToUse?.address?.full_address) {
      q += ` ${propertyToUse.address.full_address}`;
    }

    const { data } = await apiClient.get<SerpApiResponse>(SERP_SEARCH_PATH, {
      params: {
        q,
        location: userLocation,
        hl: "en",
        gl: "us",
      },
    });

    const combinedData: NewsArticle[] = [
      ...formatNewsData(data.inline_videos || []),
      ...formatNewsData(data.top_stories || []),
      ...formatNewsData(data.organic_results || []),
    ];

    return {
      success: true,
      data: combinedData,
    };
  } catch (error) {
    console.error("Error Fetching News:", error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "Failed to fetch news. Please try again later.",
      data: [],
    };
  }
};

export const formatNewsData = (newsData: unknown[]): NewsArticle[] => {
  if (!Array.isArray(newsData)) {
    return [];
  }

  return newsData.map((item) => {
    const newsItem = item as Partial<NewsArticle> & {
      description?: string;
      url?: string;
      image?: string;
      publisher?: string;
      published_date?: string;
    };

    return {
      id: newsItem.link || newsItem.id || Math.random().toString(36).substring(2, 11),
      title: newsItem.title || "No Title",
      snippet: newsItem.snippet || newsItem.description || "No description available",
      link: newsItem.link || newsItem.url || "",
      thumbnail: newsItem.thumbnail || newsItem.image || null,
      source: newsItem.source || newsItem.publisher || "Unknown",
      date: newsItem.date || newsItem.published_date || null,
      type: newsItem.type || "article",
    };
  });
};
